import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Globe } from 'lucide-react';
import LoginRoleToggle from '../components/LoginRoleToggle';
import WrongPortalHint from '../components/WrongPortalHint';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ClientLogin = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    password: '',
  });

  useEffect(() => {
    const qsEmail = searchParams.get('email');
    if (qsEmail && !formData.email) {
      setFormData((f) => ({ ...f, email: qsEmail }));
    }
  }, [searchParams, formData.email]);
  const [loading, setLoading] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'nl' ? 'en' : 'nl';
    i18n.changeLanguage(newLang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/client/login`, formData);
      
      localStorage.setItem('clientToken', response.data.access_token);
      localStorage.setItem('clientData', JSON.stringify(response.data.client));
      
      toast.success(t('loginSuccess'));
      navigate('/client/dashboard');
    } catch (error) {
      toast.error(t('invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a2a2a] via-[#0d3d3d] to-[#1a5656] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-4">
          <LoginRoleToggle />
        </div>
        <div className="mb-3"><WrongPortalHint email={formData.email} expected="klant" /></div>
        <Card className="w-full bg-[#0d3d3d]/90 border-white/20 backdrop-blur">
        <CardHeader className="space-y-4">
          <div className="flex justify-between items-center">
            <CardTitle className="text-3xl font-bold text-white">
              Cardoso Bouw
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="text-white hover:bg-white/10"
            >
              <Globe className="w-4 h-4 mr-2" />
              {i18n.language === 'nl' ? 'EN' : 'NL'}
            </Button>
          </div>
          <p className="text-white/70 text-lg">{t('welcome')}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-white mb-2 block">{t('email')}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-white/10 border-white/30 text-white"
                placeholder="voorbeeld@email.nl"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-white">{t('password')}</Label>
                <Link to={`/forgot-password?email=${encodeURIComponent(formData.email)}`} className="text-xs text-[#3da5a5] hover:text-white transition-colors" data-testid="forgot-password-link">
                  Wachtwoord vergeten?
                </Link>
              </div>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="bg-white/10 border-white/30 text-white"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#3da5a5] hover:bg-[#2d8585] text-white py-6 text-lg font-semibold"
            >
              {loading ? '...' : t('loginButton')}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-white/70 text-sm">
              {t('noAccount')}{' '}
              <Link to="/client/register" className="text-[#3da5a5] hover:text-[#2d8585] font-semibold">
                {t('registerNow')}
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default ClientLogin;
